// Contenido de «¿Cómo funciona?» para Forma de pago (06) y Cotización (07).
import {
    ArrowRight,
    CalendarDays,
    CircleCheck,
    CircleHelp,
    CircleX,
    CreditCard,
    ExternalLink,
    Lock,
    LockOpen,
    Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import type { HowItWorksContent } from './HowItWorksModal';

const SKETCH_BAND = 'rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.06em]';

/** Esquema de «Forma de pago»: resumen arriba, SureKeep y Contado, y las otras financieras. */
function PaymentMethodScreenSketch() {
    return (
        <div
            aria-hidden
            className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted/30 p-3"
        >
            <span className={cn(SKETCH_BAND, 'border-2 border-promo bg-promo-soft text-promo')}>
                Resumen
            </span>
            <span
                className={cn(SKETCH_BAND, 'border-2 border-success bg-success-soft text-success')}
            >
                SureKeep · A crédito / Enganche hoy
            </span>
            <span
                className={cn(SKETCH_BAND, 'border-2 border-success bg-card text-muted-foreground')}
            >
                Contado · A pagar
            </span>
            <span className={cn(SKETCH_BAND, 'border-2 border-info bg-info-soft text-info')}>
                Otras financieras
            </span>
        </div>
    );
}

export const PAYMENT_METHOD_HOW_IT_WORKS: HowItWorksContent = {
    sketch: <PaymentMethodScreenSketch />,
    zones: [
        {
            name: 'Resumen',
            text: 'equipo, extras, regalos y total, ya con descuentos. Sin bonos de crédito.',
            swatch: 'border-promo bg-promo-soft',
        },
        {
            name: 'SureKeep y Contado',
            text: 'lo que va a crédito y el enganche de hoy, o lo que paga de contado, con lo que gana o pierde en cada uno.',
            swatch: 'border-success bg-success-soft',
        },
        {
            name: 'Otras financieras',
            text: 'la venta sale del sistema; el cliente ve los términos con la financiera.',
            swatch: 'border-info bg-info-soft',
        },
    ],
    stepsTitle: 'Cómo elegir la forma de pago',
    steps: [
        { icon: CircleCheck, title: 'Revisa el resumen', text: 'Equipo, extras y total.' },
        {
            icon: Sparkles,
            title: 'SureKeep si quiere crédito',
            text: 'Pagos semanales y bonos de crédito.',
        },
        { icon: CreditCard, title: 'O Contado', text: 'Paga todo hoy. Sin pagos después.' },
        {
            icon: ArrowRight,
            title: 'Continuar',
            text: 'Pasa a Cotización o a Cobro, según la forma de pago.',
        },
    ],
    legendTitle: 'Elementos',
    legend: [
        {
            icon: Sparkles,
            color: 'text-promo',
            name: 'Lo que gana',
            text: 'bonos y promos que solo da SureKeep',
        },
        {
            icon: CircleX,
            color: 'text-destructive',
            name: 'Lo que pierde',
            text: 'en Contado, las promos que solo aplican con SureKeep',
        },
        {
            icon: CircleCheck,
            color: 'text-success',
            name: 'A crédito / Enganche hoy',
            text: 'cuánto financia SureKeep y cuánto paga hoy',
        },
        {
            icon: ExternalLink,
            color: 'text-info',
            name: 'Sale del sistema',
            text: 'otras financieras: el cliente ve sus términos afuera',
        },
    ],
};

/** Esquema de «Cotización»: plazo y tarjetas ligadas arriba, resumen de tres filas abajo. */
function QuoteScreenSketch() {
    return (
        <div
            aria-hidden
            className="flex flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3"
        >
            <div className="flex flex-col gap-1 rounded border-2 border-promo bg-promo-soft p-2">
                <span className="text-[10px] font-semibold text-promo">Plazo</span>
                <div className="grid grid-cols-2 gap-1.5">
                    <span className={cn(SKETCH_BAND, 'bg-card text-muted-foreground')}>
                        Enganche
                    </span>
                    <span className={cn(SKETCH_BAND, 'bg-card text-promo')}>Pago semanal</span>
                </div>
            </div>
            <div className="flex flex-col gap-1 rounded border-2 border-border bg-card p-2">
                <span className={cn(SKETCH_BAND, 'text-muted-foreground')}>Hoy en efectivo</span>
                <span className={cn(SKETCH_BAND, 'text-promo')}>A crédito</span>
                <span className={cn(SKETCH_BAND, 'bg-success-soft text-success')}>
                    Gana con crédito
                </span>
            </div>
        </div>
    );
}

export const QUOTE_HOW_IT_WORKS: HowItWorksContent = {
    sketch: <QuoteScreenSketch />,
    zones: [
        {
            name: 'Plazo, enganche y pago semanal',
            text: 'fija el enganche o el pago; el otro se calcula.',
            swatch: 'border-promo bg-promo-soft',
        },
        {
            name: 'Resumen de la cotización',
            text: 'lo que paga hoy en efectivo, lo que paga cada semana y lo que gana con crédito.',
            swatch: 'border-border bg-muted',
        },
    ],
    stepsTitle: 'Cómo cotizar',
    steps: [
        { icon: CalendarDays, title: 'Elige el plazo', text: 'Los que da el plan del cliente.' },
        { icon: Lock, title: 'Fija enganche o pago', text: 'El otro se calcula al momento.' },
        { icon: Sparkles, title: 'Revisa lo que gana', text: 'Bonos de crédito, si aplican.' },
        { icon: ArrowRight, title: 'Continuar', text: 'Pasa a cobrar el enganche.' },
    ],
    legendTitle: 'Elementos',
    legend: [
        {
            icon: Lock,
            color: 'text-promo',
            name: 'Fijo',
            text: 'el monto que pusiste; no se mueve al recotizar',
        },
        {
            icon: LockOpen,
            color: 'text-muted-foreground',
            name: 'Fijar',
            text: 'hace fijo ese monto en lugar del otro',
        },
        {
            icon: CircleHelp,
            color: 'text-promo',
            name: '«?» en las filas',
            text: 'abre el desglose del efectivo o del crédito',
        },
        {
            icon: Sparkles,
            color: 'text-success',
            name: 'Gana con crédito',
            text: 'bono que da SureKeep al activar el crédito',
        },
    ],
};
